import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useQaDetail, useQa } from './hooks/useQa';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { QaFormModal } from './components/QaFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  pending_inspection: 'bg-amber-100 text-amber-600',
  inspecting: 'bg-blue-100 text-blue-600',
  passed: 'bg-emerald-100 text-emerald-600',
  failed: 'bg-red-100 text-red-600',
  rework_required: 'bg-indigo-100 text-indigo-600',
  closed: 'bg-slate-900 text-white',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  pending_inspection: 'Chờ kiểm tra',
  inspecting: 'Đang kiểm tra',
  passed: 'Đạt chất lượng',
  failed: 'Không đạt',
  rework_required: 'Yêu cầu sửa lại',
  closed: 'Đã đóng',
};

const resultColors: Record<string, string> = {
  pass: 'text-emerald-600 bg-emerald-50 border-emerald-100',
  fail: 'text-red-600 bg-red-50 border-red-100',
  pending: 'text-slate-400 bg-slate-50 border-slate-100',
  na: 'text-slate-300 bg-slate-50 border-slate-100',
};

export default function MaintenanceQaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { inspection, items, isLoading } = useQaDetail(id);
  const { submitResult, isSubmitting } = useQa(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết kiểm tra QA...</div>;
  if (!inspection) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu kiểm tra</div>;

  const handlePass = async () => {
    try {
      await submitResult({ id: id!, score: 10, result: 'pass', notes: 'Đạt yêu cầu qua kiểm tra nhanh.' });
      toast.success('Đã xác nhận đạt chất lượng');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'checklist', label: 'Hạng mục kiểm tra' },
    { value: 'media', label: 'Ảnh minh chứng' },
    { value: 'rework', label: 'Lỗi & Làm lại' },
  ];

  return (
    <PageDetailLayout
      title={`Kiểm tra: ${inspection.code}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[inspection.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[inspection.status_code] || inspection.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{inspection.code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-user-follow-line" />
            {inspection.inspector?.full_name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'QA Kiểm soát chất lượng', path: '/maintenance/qa' },
        { label: inspection.code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {inspection.status_code === 'pending_inspection' && (
            <AuthButton module="maintenance" action="inspect" variant="primary" icon="ri-shield-check-line" onClick={handlePass} isLoading={isSubmitting}>
              Duyệt nhanh (Pass)
            </AuthButton>
          )}
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Điểm tổng kết" value={inspection.overall_score || 0} icon="ri-star-line" variant="warning" suffix="/10" />
                <KPICard title="Hạng mục lỗi" value={inspection.failed_items_count || 0} icon="ri-error-warning-line" variant="danger" />
                <KPICard title="Kết quả" value={inspection.final_result?.toUpperCase() || '---'} icon="ri-shield-check-line" variant="info" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin đợt kiểm tra
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{inspection.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Lệnh công việc (WO)</span>
                    <span className="text-sm font-bold text-os-primary font-mono">{inspection.work_order?.order_number || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Bộ tiêu chuẩn</span>
                    <span className="text-sm font-bold text-slate-700">{inspection.checklist?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày kiểm tra</span>
                    <span className="text-sm font-bold text-slate-700">{inspection.inspection_date ? new Date(inspection.inspection_date).toLocaleDateString('vi-VN') : 'Chưa kiểm tra'}</span>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold text-slate-400  mb-2">Ghi chú của thanh tra</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 italic leading-relaxed">
                    {inspection.notes || 'Không có ghi chú đánh giá chi tiết cho đợt kiểm tra này.'}
                  </p>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <i className="ri-list-check-line text-os-primary" />
                    Kết quả chi tiết các hạng mục
                  </h3>
                  <Badge variant="neutral" className="bg-slate-100 text-slate-600">{items.length} tiêu chuẩn</Badge>
                </div>
                <div className="space-y-3">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-os-primary/30 transition-all">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-800">{item.checklist_item?.name}</span>
                        <span className="text-[10px] text-slate-500 line-clamp-1">{item.checklist_item?.description}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-bold text-slate-400">Điểm: {item.score || 0}</span>
                        <Badge className={`${resultColors[item.result_code]} border-transparent text-[10px] font-bold `}>
                          {item.result_code}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-sm text-slate-400 italic py-4 text-center">Chưa có hạng mục kiểm tra nào được ghi nhận.</p>}
                </div>
              </Card>
            </>
          )}

          {(activeTab === 'media' || activeTab === 'rework') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Dữ liệu hình ảnh minh chứng và yêu cầu làm lại đang được tổng hợp."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-history-line text-os-primary" />
              Tóm tắt chất lượng
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400  font-bold tracking-wider">Trạng thái</span>
                <Badge className={`${statusColors[inspection.status_code]} border-transparent text-[10px] font-bold `}>
                  {statusLabels[inspection.status_code]}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Tỉ lệ hoàn thành</span>
                <span className="font-bold">{items.length > 0 ? Math.round((items.filter(i => i.result_code !== 'pending').length / items.length) * 100) : 0}%</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                {inspection.final_result === 'pass' ? (
                  <Alert variant="success" icon="ri-checkbox-circle-line">
                    Đã đạt tiêu chuẩn chất lượng và có thể đóng lệnh công việc.
                  </Alert>
                ) : (
                  <Alert variant="warning" icon="ri-error-warning-line">
                    Cần hoàn thành kiểm tra hoặc xử lý các hạng mục lỗi trước khi đóng lệnh.
                  </Alert>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-shield-user-line text-slate-400" />
              Thanh tra phụ trách
            </h4>
            <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <Avatar src={inspection.inspector?.avatar_url} name={inspection.inspector?.full_name} size="lg" />
              <div>
                <p className="text-sm font-bold text-slate-900">{inspection.inspector?.full_name}</p>
                <p className="text-xs text-slate-500">Chuyên viên kiểm soát chất lượng</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <QaFormModal
        isOpen={showEdit}
        item={inspection}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
